import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AptitudeLanding from './AptitudeLanding';
import TestSelectionModal from './TestSelectionModal';
import MathLanguageModal from './MathLanguageModal';
import TestComponent from './TestComponent';

type FlowState = 'landing' | 'test-selection' | 'math-language' | 'testing';

interface TestConfig {
  testType: 'english' | 'math';
  testLanguage: 'english' | 'arabic';
}

interface TestResults {
  score: number;
  totalQuestions: number;
}

const AptitudeTestController: React.FC = () => {
  const navigate = useNavigate();
  const [flowState, setFlowState] = useState<FlowState>('landing');
  const [language, setLanguage] = useState<'ar' | 'en'>('ar');
  const [testConfig, setTestConfig] = useState<TestConfig>({
    testType: 'english',
    testLanguage: 'english'
  });

  const handleLanguageToggle = () => {
    setLanguage(prev => prev === 'ar' ? 'en' : 'ar');
  };

  const handleStartTest = () => {
    setFlowState('test-selection');
  };

  const handleTestSelection = (testType: 'english' | 'math') => {
    setTestConfig(prev => ({ ...prev, testType }));
    
    if (testType === 'english') {
      setTestConfig(prev => ({ ...prev, testLanguage: 'english' }));
      setFlowState('testing');
    } else {
      setFlowState('math-language');
    }
  };

  const handleMathLanguageSelection = (testLanguage: 'english' | 'arabic') => {
    setTestConfig(prev => ({ ...prev, testLanguage }));
    setFlowState('testing');
  };

  const handleTestComplete = (score: number, totalQuestions: number) => {
    navigate('/aptitude-results', {
      state: {
        score,
        totalQuestions,
        testType: testConfig.testType,
        language
      }
    });
  };

  const handleRetakeTest = () => {
    setFlowState('test-selection');
  };

  const handleGoHome = () => {
    setFlowState('landing');
  };

  const handleBackToLanding = () => {
    setFlowState('landing');
  };

  const handleBackToTestSelection = () => {
    setFlowState('test-selection');
  };

  const handleCloseModal = () => {
    setFlowState('landing');
  };

  switch (flowState) {
    case 'landing':
      return (
        <AptitudeLanding
          onStartTest={handleStartTest}
          language={language}
          onLanguageToggle={handleLanguageToggle}
        />
      );

    case 'test-selection':
      return (
        <>
          <AptitudeLanding
            onStartTest={handleStartTest}
            language={language}
            onLanguageToggle={handleLanguageToggle}
          />
          <TestSelectionModal
            isOpen={true}
            onClose={handleCloseModal}
            onSelectTest={handleTestSelection}
            language={language}
          />
        </>
      );

    case 'math-language':
      return (
        <>
          <AptitudeLanding
            onStartTest={handleStartTest}
            language={language}
            onLanguageToggle={handleLanguageToggle}
          />
          <MathLanguageModal
            isOpen={true}
            onClose={handleBackToTestSelection}
            onSelectLanguage={handleMathLanguageSelection}
            language={language}
          />
        </>
      );

    case 'testing':
      return (
        <TestComponent
          testType={testConfig.testType}
          testLanguage={testConfig.testLanguage}
          onTestComplete={handleTestComplete}
          onBack={handleBackToLanding}
          language={language}
        />
      );

    default:
      return (
        <AptitudeLanding
          onStartTest={handleStartTest}
          language={language}
          onLanguageToggle={handleLanguageToggle}
        />
      );
  }
};

export default AptitudeTestController;
